import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { RequireAuth } from './auth/RequireAuth';
import { canManage, isSupporting, useSession } from './auth/session';
import { AppShell } from './components/AppShell';
import { AdminCompaniesPage } from './pages/admin/CompaniesPage';
import { AdminCompanyDetailPage } from './pages/admin/CompanyDetailPage';
import { AdminDevicesPage } from './pages/admin/DevicesPage';
import { AdminSuperAdminsPage } from './pages/admin/SuperAdminsPage';
import { AlertsPage } from './pages/AlertsPage';
import { CountDetailPage } from './pages/CountsPage';
import { TransferDetailPage } from './pages/TransferDetailPage';
import { RequestDetailPage, RequestsPage } from './pages/RequestsPage';
import { ProjectDetailPage, ProjectsPage } from './pages/ProjectsPage';
import { ReportsPage } from './pages/ReportsPage';
import { RatesPage } from './pages/settings/RatesPage';
import { SupportLogPage } from './pages/settings/SupportLogPage';
import { LabelLinkPage } from './pages/LabelLinkPage';
import { ZoneLinkPage } from './pages/ZoneLinkPage';
import { AssetDetailPage } from './pages/AssetDetailPage';
import { AssetsPage } from './pages/AssetsPage';
import { AssetImportPage } from './pages/AssetImportPage';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { CategoriesPage } from './pages/settings/CategoriesPage';
import { ApprovalsPage } from './pages/settings/ApprovalsPage';
import { NotificationsPage } from './pages/settings/NotificationsPage';
import { UsersPage } from './pages/settings/UsersPage';
import { PlacePage } from './pages/PlacePage';
import {
  CountsRedirect,
  HomeListRedirect,
  LabelsRedirect,
  SiteRedirect,
  SiteSettingsRedirect,
} from './pages/SiteRedirect';
import { WarehousesPage } from './pages/WarehousesPage';

/** The admin area is for FleetSpoke super admins, and not while supporting a customer (S7); RLS enforces it anyway. */
function PlatformAdminOnly({ children }: { children: ReactNode }) {
  const { me } = useSession();
  return me?.platform_admin && !isSupporting(me) ? <>{children}</> : <Navigate to="/" replace />;
}

/** Settings are for managers and super admins; RLS enforces it anyway — this only avoids dead-end screens. */
function ManagerOnly({ children }: { children: ReactNode }) {
  const { me } = useSession();
  return canManage(me) ? <>{children}</> : <Navigate to="/" replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="warehouses" element={<WarehousesPage />} />
          <Route
            path="warehouses/:siteId"
            element={<PlacePage key="warehouse" kind="warehouse" />}
          />
          <Route path="locations/:siteId" element={<PlacePage key="location" kind="location" />} />
          {/* old links (P14) */}
          <Route path="sites" element={<HomeListRedirect />} />
          <Route path="sites/:siteId" element={<SiteRedirect />} />
          <Route path="map" element={<HomeListRedirect />} />
          <Route path="counts" element={<CountsRedirect />} />
          <Route path="settings/sites" element={<SiteSettingsRedirect />} />
          {/* label printing moved to the Assets list and the places' Assets tab (open to everyone) */}
          <Route path="settings/labels" element={<LabelsRedirect />} />
          <Route path="assets" element={<AssetsPage />} />
          {/* before assets/:assetId, so "import" is not read as an asset id (CR_20260920_Asset_Register Q4) */}
          <Route
            path="assets/import"
            element={
              <ManagerOnly>
                <AssetImportPage />
              </ManagerOnly>
            }
          />
          <Route path="assets/:assetId" element={<AssetDetailPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          {/* the list left with R0 (CR_20260915_Request_Qty Q15): transfers live on their places' Transfers tab */}
          <Route path="transfers" element={<Navigate to="/" replace />} />
          <Route path="transfers/:transferId" element={<TransferDetailPage />} />
          <Route path="requests" element={<RequestsPage />} />
          <Route path="requests/:requestId" element={<RequestDetailPage />} />
          <Route path="counts/:countId" element={<CountDetailPage />} />
          <Route path="a/:companyCode/:assetCode" element={<LabelLinkPage />} />
          <Route path="z/:companyCode/:zoneId" element={<ZoneLinkPage />} />
          <Route
            path="reports"
            element={
              <ManagerOnly>
                <ReportsPage />
              </ManagerOnly>
            }
          />
          {/* everyone with the project in their access; money is hidden inside for non-managers (P7) */}
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/:projectId" element={<ProjectDetailPage />} />
          <Route
            path="settings/rates"
            element={
              <ManagerOnly>
                <RatesPage />
              </ManagerOnly>
            }
          />
          <Route
            path="settings/support"
            element={
              <ManagerOnly>
                <SupportLogPage />
              </ManagerOnly>
            }
          />
          <Route
            path="admin/companies"
            element={
              <PlatformAdminOnly>
                <AdminCompaniesPage />
              </PlatformAdminOnly>
            }
          />
          <Route
            path="admin/companies/:companyId"
            element={
              <PlatformAdminOnly>
                <AdminCompanyDetailPage />
              </PlatformAdminOnly>
            }
          />
          <Route
            path="admin/devices"
            element={
              <PlatformAdminOnly>
                <AdminDevicesPage />
              </PlatformAdminOnly>
            }
          />
          <Route
            path="admin/super-admins"
            element={
              <PlatformAdminOnly>
                <AdminSuperAdminsPage />
              </PlatformAdminOnly>
            }
          />
          <Route
            path="settings/users"
            element={
              <ManagerOnly>
                <UsersPage />
              </ManagerOnly>
            }
          />
          <Route
            path="settings/notifications"
            element={
              <ManagerOnly>
                <NotificationsPage />
              </ManagerOnly>
            }
          />
          <Route
            path="settings/categories"
            element={
              <ManagerOnly>
                <CategoriesPage />
              </ManagerOnly>
            }
          />
          <Route
            path="settings/approvals"
            element={
              <ManagerOnly>
                <ApprovalsPage />
              </ManagerOnly>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
